import React, { useState } from 'react';
import { X, Coins, ShoppingCart, RefreshCw } from 'lucide-react';
import { CREDIT_EXPIRY_COPY, formatV3UnitRate, minorToMajor, REHUB_CREDIT_MONTHLY_PLANS_V3, REHUB_CREDIT_TOPUPS_V3 } from '@rehub/pricing';

type Currency = 'GBP' | 'USD';

function defaultCurrency(): Currency {
  if (typeof window === 'undefined') return 'USD';
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Europe/London') return 'GBP';
    if ((navigator.language || '').startsWith('en-GB')) return 'GBP';
  } catch (_) {}
  return 'USD';
}

// Derived from the live @rehub/pricing catalogue — do not hardcode amounts here.
const TOPUP_PACKS = REHUB_CREDIT_TOPUPS_V3.map((p) => ({
  credits: p.credits,
  GBP: minorToMajor(p.amountMinor.GBP),
  USD: minorToMajor(p.amountMinor.USD),
  perCreditGBP: formatV3UnitRate(p.amountMinor.GBP, p.credits, 'GBP'),
  perCreditUSD: formatV3UnitRate(p.amountMinor.USD, p.credits, 'USD'),
  popular: p.badge === 'popular',
}));

const SUBSCRIPTION_PLANS = REHUB_CREDIT_MONTHLY_PLANS_V3.map((p) => ({
  name: p.label,
  credits: p.credits,
  GBP: minorToMajor(p.amountMinor.GBP),
  USD: minorToMajor(p.amountMinor.USD),
  perCreditGBP: formatV3UnitRate(p.amountMinor.GBP, p.credits, 'GBP'),
  perCreditUSD: formatV3UnitRate(p.amountMinor.USD, p.credits, 'USD'),
  popular: p.badge === 'popular',
}));

interface PricingBenefitsModalProps {
  onClose: () => void;
  onSignUp?: () => void;
}

const PricingBenefitsModal: React.FC<PricingBenefitsModalProps> = ({ onClose, onSignUp }) => {
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const sym = currency === 'GBP' ? '£' : '$';

  return (
    <div
      className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-slate-700 px-6 py-4 z-10">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="shrink-0">
              <a href="https://hub.rehub.studio" target="_blank" rel="noopener noreferrer" className="inline-flex opacity-95 hover:opacity-100 transition-opacity">
                <img src="/ReHub%20Logo%20Transparent%20White%20Text%20.png" alt="ReHub Studio" className="h-14 w-auto object-contain" />
              </a>
            </div>
            <div className="flex items-center gap-2">
              {(['GBP', 'USD'] as Currency[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${currency === c ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-slate-200'}`}
                >
                  {c === 'GBP' ? '£ UK' : '$ US'}
                </button>
              ))}
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors shrink-0"
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>
          </div>
          <h2 className="text-xl font-bold text-white">PhotoClear Pricing</h2>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Credits intro */}
          <section>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-teal-900/20 border border-teal-800/40">
              <Coins size={20} className="text-teal-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-teal-300">1 credit per declutter</p>
                <p className="text-sm text-slate-400 mt-1">
                  10 free credits on sign-up. Same credits work across all ReHub apps — buy once, use anywhere.
                </p>
              </div>
            </div>
          </section>

          {/* One-time top-ups */}
          <section>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
              <ShoppingCart size={16} className="text-teal-400" />
              One-time credit packs
            </h3>
            <div className="rounded-xl border border-slate-700 overflow-hidden">
              <div className="grid grid-cols-3 gap-3 px-4 py-2.5 bg-slate-800/60 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <span>Credits</span>
                <span className="text-right">Price</span>
                <span className="text-right">Per credit</span>
              </div>
              {TOPUP_PACKS.map((pack, i) => (
                <div
                  key={pack.credits}
                  className={`grid grid-cols-3 gap-3 px-4 py-2.5 items-center ${i > 0 ? 'border-t border-slate-800' : ''} ${pack.popular ? 'bg-teal-900/10' : ''}`}
                >
                  <span className="text-slate-200 font-medium">
                    {pack.credits}
                    {pack.popular && <span className="ml-2 text-[10px] font-bold text-teal-400 uppercase">Popular</span>}
                  </span>
                  <span className="text-right text-slate-300 tabular-nums">
                    {sym}{currency === 'GBP' ? pack.GBP.toFixed(2) : pack.USD.toFixed(2)}
                  </span>
                  <span className="text-right text-teal-400 font-semibold tabular-nums text-sm">
                    {currency === 'GBP' ? pack.perCreditGBP : pack.perCreditUSD}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Monthly subscriptions */}
          <section>
            <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3">
              <RefreshCw size={16} className="text-teal-400" />
              Monthly credit plans
            </h3>
            <div className="rounded-xl border border-slate-700 overflow-hidden">
              <div className="grid grid-cols-4 gap-2 px-4 py-2.5 bg-slate-800/60 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <span>Plan</span>
                <span className="text-right">Credits/mo</span>
                <span className="text-right">Price</span>
                <span className="text-right">Per credit</span>
              </div>
              {SUBSCRIPTION_PLANS.map((plan, i) => (
                <div
                  key={plan.name}
                  className={`grid grid-cols-4 gap-2 px-4 py-2.5 items-center ${i > 0 ? 'border-t border-slate-800' : ''} ${plan.popular ? 'bg-teal-900/10' : ''}`}
                >
                  <span className="text-slate-200 font-medium">
                    {plan.name}
                    {plan.popular && <span className="ml-1.5 text-[10px] font-bold text-teal-400 uppercase">Popular</span>}
                  </span>
                  <span className="text-right text-slate-300 tabular-nums">{plan.credits.toLocaleString()}</span>
                  <span className="text-right text-slate-300 tabular-nums">
                    {sym}{currency === 'GBP' ? plan.GBP.toFixed(2) : plan.USD.toFixed(2)}/mo
                  </span>
                  <span className="text-right text-teal-400 font-semibold tabular-nums text-sm">
                    {currency === 'GBP' ? plan.perCreditGBP : plan.perCreditUSD}
                  </span>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">{CREDIT_EXPIRY_COPY.benefitsModal}</p>
          </section>

          {/* CTA */}
          <div className="pt-2 pb-1">
            {onSignUp ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onSignUp();
                }}
                className="block w-full py-4 px-6 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-xl transition-colors shadow-lg hover:shadow-teal-500/25 text-center"
              >
                Sign up — 10 free credits, no card required
              </button>
            ) : (
              <a
                href="https://photoclear.rehub.studio"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full py-4 px-6 bg-teal-600 hover:bg-teal-500 text-white font-semibold rounded-xl transition-colors shadow-lg hover:shadow-teal-500/25 text-center"
              >
                Sign up — 10 free credits, no card required
              </a>
            )}
            <p className="text-center text-slate-500 text-xs mt-3">
              Already have an account? You can sign in from the main page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingBenefitsModal;
